import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { ColorPicker } from '@/components/ColorPicker';
import { Tooltip } from '@/components/Tooltip';
import { PageModal } from '@/components/modals/PageModal';
import { MapOverlayKmlScreen } from '@/components/screens/MapOverlayKmlScreen';
import { renderReactComponent, destroyReactComponent } from '@/react-bridge';
import { navigate } from '@/services/navigation';
import { useGoogleMap } from '@/hooks/useGoogleMap';
import {
  POLYGON_OVERLAY_NORMAL_COLOR,
  POLYGON_OVERLAY_NORMAL_STROKE_OPACITY,
  POLYGON_OVERLAY_PATROL_ZONE_STROKE_OPACITY as POLYGON_OVERLAY_PATROL_ZONEL_STROKE_OPACITY,
  NOT_VISIBLE_ICON_SVG,
} from '@/constants/map-styles';
import {
  CurrentMap,
  deselectAllPolygons,
  resetOverlayZIndexes,
  newGoogleMapsPolygon,
  centerMapOnCurrentLocation,
  updateFiltersToCacheAndLocalStorage,
  layerSwitchClicked,
  setupMapCore,
  initAutocompleteTemp,
  initMap,
  setUpMapOverlay,
  resetMapRefresh,
  setMapScreenLoaded,
} from '@/services/map-service';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { hasStringValue, hasValue } from '@/utils/legacy-utils';
import { getCurrentCompanyId } from '@/utils/auth';
import { AccessAuthorities } from '@/models';

// ── Global Declarations ──────────────────────────────────────────────

import { getAvailableSubmodules, getOfficerPluralLabel } from '@/utils/auth';
import { isInArray, removeFromArray } from '@/utils/arrays';
import { ModuleType } from '@/constants/codes';
import { CallCardStatus } from '@/services/api-types';
// Constructors: access lazily via (window as any).X at usage sites
// google: kept as (window as any).google since loaded by script tag

// ── Types ────────────────────────────────────────────────────────────

interface MapScreenProps {
  moduleType?: 'cad' | 'mdt';
  callCardId?: string;
  address?: string;
}

// ── Component ────────────────────────────────────────────────────────

export function MapScreen({ moduleType = 'mdt', callCardId, address }: MapScreenProps) {
  const { theme } = useTheme();
  useGoogleMap();

  const isCad = moduleType === 'cad';
  const initialShowTrackMe = !isCad;

  // Map options / overlay panel state
  const [areMapOptionsShown, setAreMapOptionsShown] = useState(false);
  const [areMapOverlayOptionsShown, setAreMapOverlayOptionsShown] = useState(false);
  const [isEditingPolygonOverlay, setIsEditingPolygonOverlay] = useState(false);
  const [isSelectedIsVisible, setIsSelectedIsVisible] = useState(false);
  const [isSelectedPolygonPatrolZone, setIsSelectedPolygonPatrolZone] = useState(false);
  const [isSelectedPolygonEditable, setIsSelectedPolygonEditable] = useState(false);
  const [isSelectedPolygonDraggable, setIsSelectedPolygonDraggable] = useState(false);
  const [selectedPolygonLabelText, setSelectedPolygonLabelText] = useState('');
  const [selectedPolygonColor, setSelectedPolygonColor] = useState<string | null>(null);
  const [isPolygonModified, setIsPolygonModified] = useState(false);

  // Custom location dialog
  const [addCustomLocationDialogLocation, setAddCustomLocationDialogLocation] = useState<string | null>(null);
  const [addCustomLocationDialogCoordinates, setAddCustomLocationDialogCoordinates] = useState<string | null>(null);
  const [showAddCustomLocationModal, setShowAddCustomLocationModal] = useState(false);
  const [isSavingCustomLocation, setIsSavingCustomLocation] = useState<boolean>(false);

  // Delete area confirm
  const [showDeleteAreaModal, setShowDeleteAreaModal] = useState(false);

  // Color picker
  const [showColorPicker, setShowColorPicker] = useState(false);

  // KML modal
  const [showKmlModal, setShowKmlModal] = useState(false);

  // Track-me buttons
  const [showTrackMeButtons, setShowTrackMeButtons] = useState(initialShowTrackMe);
  const [isTrackingMe, setIsTrackingMe] = useState(false);

  // Call location change modal
  const [showChangeCallLocationModal, setShowChangeCallLocationModal] = useState(false);
  const [pendingCallLocationChange, setPendingCallLocationChange] = useState<{ marker: any; event: any } | null>(null);

  // Init tracking
  const [mapInitialized, setMapInitialized] = useState(false);

  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);
  const hasMapOverlayAreaVisibilityPermission = useMemo(() => {
    return isInArray(getAvailableSubmodules(), AccessAuthorities.ROLE_MAP_OVERLAY_AREA_VISIBILITY);
  }, []);

  const showOverlayButton = useMemo(() => {
    const submodules: string[] = getAvailableSubmodules();
    return isInArray(submodules, AccessAuthorities.ROLE_UPDATE_MAP_OVERLAY);
  }, []);

  // Watch: isSelectedIsVisible
  useEffect(() => {
    if (!mapInitialized) return;
    if (CurrentMap && CurrentMap.selectedPolygonOverlay) {
      CurrentMap.selectedPolygonOverlay.isVisible = isSelectedIsVisible;
      if (isSelectedIsVisible) {
        CurrentMap.selectedPolygonOverlay.label.set('icon', null);
      } else {
        CurrentMap.selectedPolygonOverlay.label.set('icon', NOT_VISIBLE_ICON_SVG);
      }
    }
    setIsPolygonModified(true);
  }, [isSelectedIsVisible]);

  // Watch: isSelectedPolygonPatrolZone
  useEffect(() => {
    if (!mapInitialized) return;
    if (CurrentMap && CurrentMap.selectedPolygonOverlay) {
      CurrentMap.selectedPolygonOverlay.isPatrolZone = isSelectedPolygonPatrolZone;
      if (isSelectedPolygonPatrolZone) {
        CurrentMap.selectedPolygonOverlay.setOptions({
          strokeOpacity: POLYGON_OVERLAY_PATROL_ZONEL_STROKE_OPACITY,
        });
      } else {
        CurrentMap.selectedPolygonOverlay.setOptions({
          strokeOpacity: POLYGON_OVERLAY_NORMAL_STROKE_OPACITY,
        });
      }
    }
  }, [isSelectedPolygonPatrolZone]);

  // Watch: isSelectedPolygonEditable
  useEffect(() => {
    if (!mapInitialized) return;
    if (CurrentMap && CurrentMap.selectedPolygonOverlay) {
      CurrentMap.selectedPolygonOverlay.setEditable(isSelectedPolygonEditable);
    }
  }, [isSelectedPolygonEditable]);

  // Watch: isSelectedPolygonDraggable
  useEffect(() => {
    if (!mapInitialized) return;
    if (CurrentMap && CurrentMap.selectedPolygonOverlay) {
      CurrentMap.selectedPolygonOverlay.setDraggable(isSelectedPolygonDraggable);
    }
  }, [isSelectedPolygonDraggable]);

  // Watch: selectedPolygonLabelText
  useEffect(() => {
    if (!mapInitialized) return;
    if (!CurrentMap || !CurrentMap.selectedPolygonOverlay) return;
    if (CurrentMap.selectedPolygonOverlay.label) {
      const currentLabelText = CurrentMap.selectedPolygonOverlay.label.get('text');
      if (selectedPolygonLabelText !== currentLabelText) {
        CurrentMap.selectedPolygonOverlay.label.set('text', selectedPolygonLabelText);
        setIsPolygonModified(true);
      }
    }
  }, [selectedPolygonLabelText]);

  // Watch: selectedPolygonColor
  useEffect(() => {
    if (!mapInitialized) return;
    if (CurrentMap && CurrentMap.selectedPolygonOverlay && selectedPolygonColor) {
      CurrentMap.selectedPolygonOverlay.color = selectedPolygonColor;
      CurrentMap.selectedPolygonOverlay.setOptions({
        strokeColor: selectedPolygonColor,
        fillColor: selectedPolygonColor,
      });
    }
  }, [selectedPolygonColor]);

  // ── Methods ──────────────────────────────────────────────────────

  const getDefaultPolygonCoordinates = useCallback(() => {
    const curLatLng = CurrentMap.map.getCenter();
    const curLat = curLatLng.lat();
    const curLng = curLatLng.lng();
    const offset = 0.005;
    return [
      { lat: curLat - offset, lng: curLng + offset },
      { lat: curLat + offset, lng: curLng + offset },
      { lat: curLat + offset, lng: curLng - offset },
      { lat: curLat - offset, lng: curLng - offset },
    ];
  }, []);

  const newGoogleMapsPolygonFromToolbar = useCallback(() => {
    deselectAllPolygons();
    setIsSelectedIsVisible(true);
    setIsSelectedPolygonPatrolZone(false);
    setIsSelectedPolygonEditable(true);
    setIsSelectedPolygonDraggable(false);
    setSelectedPolygonLabelText('');
    setIsPolygonModified(true);

    const polygon = newGoogleMapsPolygon(
      null,
      getDefaultPolygonCoordinates(),
      '',
      false,
      true,
      true,
      POLYGON_OVERLAY_NORMAL_COLOR
    );
    setSelectedPolygonColor(POLYGON_OVERLAY_NORMAL_COLOR);

    CurrentMap.polygonOverlays.push(polygon);
    CurrentMap.selectedPolygonOverlay = polygon;
    setIsEditingPolygonOverlay(true);

    return polygon;
  }, [getDefaultPolygonCoordinates]);

  const openNav = useCallback(() => {
    deselectAllPolygons();
    setAreMapOptionsShown(true);
    setAreMapOverlayOptionsShown(false);
  }, []);

  const closeNavAndOverlayOptions = useCallback(() => {
    setAreMapOptionsShown(false);
    setAreMapOverlayOptionsShown(false);
    deselectAllPolygons();
    setIsEditingPolygonOverlay(false);
  }, []);

  const openOverlayOptions = useCallback(() => {
    deselectAllPolygons();
    setIsEditingPolygonOverlay(false);
    setAreMapOptionsShown(false);
    setAreMapOverlayOptionsShown(true);
  }, []);

  const addPolygonOverlay = useCallback(() => {
    const polygon = newGoogleMapsPolygonFromToolbar();
    polygon.setMap(CurrentMap.map);
    polygon.label.setMap(CurrentMap.map);
    resetOverlayZIndexes();
  }, [newGoogleMapsPolygonFromToolbar]);

  const removePolygonOverlay = useCallback(() => {
    if (!CurrentMap.selectedPolygonOverlay) return;
    setShowDeleteAreaModal(true);
  }, []);

  const confirmDeleteArea = useCallback(() => {
    if (CurrentMap.selectedPolygonOverlay) {
      removeFromArray(CurrentMap.polygonOverlays, CurrentMap.selectedPolygonOverlay);
      if (CurrentMap.selectedPolygonOverlay.label) {
        CurrentMap.selectedPolygonOverlay.label.set('map', null);
        CurrentMap.selectedPolygonOverlay.label = null;
      }
      CurrentMap.selectedPolygonOverlay.setMap(null);
      CurrentMap.selectedPolygonOverlay = null;
      setIsPolygonModified(true);
      setIsEditingPolygonOverlay(false);
    }
    setShowDeleteAreaModal(false);
  }, []);

  const movePolygonOverlayToFront = useCallback(() => {
    if (CurrentMap.selectedPolygonOverlay) {
      const selectedOverlay = CurrentMap.selectedPolygonOverlay;
      const index = CurrentMap.polygonOverlays.indexOf(selectedOverlay);
      if (index > -1) {
        CurrentMap.polygonOverlays.splice(index, 1);
        CurrentMap.polygonOverlays.push(selectedOverlay);
      }
      resetOverlayZIndexes();
    }
  }, []);

  const movePolygonOverlayToBack = useCallback(() => {
    if (CurrentMap.selectedPolygonOverlay) {
      const selectedOverlay = CurrentMap.selectedPolygonOverlay;
      const index = CurrentMap.polygonOverlays.indexOf(selectedOverlay);
      if (index > -1) {
        CurrentMap.polygonOverlays.splice(index, 1);
        CurrentMap.polygonOverlays.unshift(selectedOverlay);
      }
      resetOverlayZIndexes();
    }
  }, []);

  const savePolygons = useCallback(() => {
    const polygonArray: any[] = [];
    try {
      for (const polygon of CurrentMap.polygonOverlays) {
        const paths = polygon.getPaths();
        let path: any = null;
        const latLngArray: any[] = [];
        for (let i = 0; i < paths.getLength(); i++) {
          path = paths.getAt(i);
          for (let ii = 0; ii < path.getLength(); ii++) {
            const coord = path.getAt(ii);
            latLngArray.push({ lat: coord.lat(), lng: coord.lng() });
          }
        }
        if (latLngArray.length > 0) {
          latLngArray.push(latLngArray[0]);
        }
        polygonArray.push({
          id: polygon.id,
          polygon: latLngArray,
          label: polygon.label ? polygon.label.get('text') : '',
          isVisible: polygon.isVisible,
          isPatrolZone: polygon.isPatrolZone,
          color: polygon.color.substring(1),
        });
      }
    } catch (err) {
      App.showPopupNotification('Failed to update map overlay. Please refresh and try again.', 'error');
      return;
    }

    api.updateMapOverlay({ polygonOverlays: polygonArray }, () => {
      setIsPolygonModified(false);
      deselectAllPolygons();
    }, () => {
      App.showPopupNotification('Failed to update map overlay.', 'error');
    });
  }, []);

  const handleColorSelect = useCallback((color: string) => {
    setSelectedPolygonColor(color);
    setIsPolygonModified(true);
    setShowColorPicker(false);
  }, []);

  const closeColorPicker = useCallback(() => {
    setShowColorPicker(false);
  }, []);

  const closeAddCustomLocationModal = useCallback(() => {
    setShowAddCustomLocationModal(false);
  }, []);

  const saveCustomLocationFromDialog = useCallback(() => {
    if (!hasStringValue(addCustomLocationDialogLocation)) {
      App.showPopupNotification('Location required.', 'error');
      return;
    }
    const coordinates = addCustomLocationDialogCoordinates;
    const coordinatesArr = coordinates!.split(',');
    const latitude = parseFloat(coordinatesArr[0]);
    const longitude = parseFloat(coordinatesArr[1]);

    const customLocationData = {
      location: addCustomLocationDialogLocation,
      latitude,
      longitude,
    };
    setIsSavingCustomLocation(true);
    api.saveCustomLocation(
      customLocationData,
      () => {
        setIsSavingCustomLocation(false);
        setShowAddCustomLocationModal(false);
        navigate({
          section: 'CAD',
          tab: 'NewCallCard',
          fn: 'CAD_CallCard',
          multiModule: true,
          state: {
            customLocationName: addCustomLocationDialogLocation,
          },
        });
      },
      () => {
        setIsSavingCustomLocation(false);
        App.showPopupNotification('Failed to save custom location.', 'error');
      }
    );
  }, [addCustomLocationDialogLocation, addCustomLocationDialogCoordinates]);

  const onFilterCheckboxChange = useCallback(() => {
    updateFiltersToCacheAndLocalStorage();
  }, []);

  const onLayerSwitchChange = useCallback((layerType: string) => {
    if (layerType === 'traffic') {
      layerSwitchClicked('trafficLayerSwitch', CurrentMap.trafficLayer);
    } else if (layerType === 'transit') {
      layerSwitchClicked('transitLayerSwitch', CurrentMap.transitLayer);
    } else if (layerType === 'bike') {
      layerSwitchClicked('bikeLayerSwitch', CurrentMap.bikeLayer);
    }
  }, []);

  const onTrackMeClick = useCallback(() => {
    if (CurrentMap.centerMapOnYou) {
      setIsTrackingMe(false);
      CurrentMap.centerMapOnYou = false;
    } else {
      setIsTrackingMe(true);
      CurrentMap.centerMapOnYou = true;
      centerMapOnCurrentLocation();
    }
  }, []);

  const onCenterMeClick = useCallback(() => {
    centerMapOnCurrentLocation();
  }, []);

  const confirmChangeCallLocation = useCallback(() => {
    if (pendingCallLocationChange) {
      const { marker, event } = pendingCallLocationChange;
      api.getCallCardById(marker['callCard'].id, false, (callCard: any) => {
        // The dispatcher placed the pin for the same address: keep the address, record the
        // pin as human-placed so it no longer reads as approximate or not found.
        callCard.latitude = event.latLng.lat();
        callCard.longitude = event.latLng.lng();
        // Non-pilot companies keep the column at LEGACY, as every other location save does.
        if (App.useGeocodingProxy()) {
          callCard.addressVerificationStatus = 'MANUAL';
        }
        // A dragged pin is no longer the learned store's answer.
        callCard.learnedAddressId = null;

        api.updateCallCard(callCard, () => {}, () => {});
      });
    }
    setShowChangeCallLocationModal(false);
    setPendingCallLocationChange(null);
  }, [pendingCallLocationChange]);

  // Board rows carry what a move would stop using, so the dialog can name it without a request.
  const changeCallLocationMessage = useMemo(() => {
    const row = pendingCallLocationChange?.marker?.['callCard'];
    if (row?.addressVerificationStatus === 'OFFICER_ON_SCENE') {
      const name = typeof row.addressVerifiedByName === 'string' ? row.addressVerifiedByName.trim() : '';
      return name
        ? `Move the call here? The on-scene location confirmed by ${name} will no longer be used for new calls.`
        : 'Move the call here? The on-scene location confirmed for this address will no longer be used for new calls.';
    }
    if (row?.hasLearnedLocation === true) {
      return 'Move the call here? The learned location for this address will no longer be used for new calls.';
    }
    return 'Are you sure you want to change the location of this call card?';
  }, [pendingCallLocationChange]);

  const cancelChangeCallLocation = useCallback(() => {
    if (pendingCallLocationChange) {
      const { marker } = pendingCallLocationChange;
      marker.setPosition({
        lat: CurrentMap.originalMarkerPosition.lat(),
        lng: CurrentMap.originalMarkerPosition.lng(),
      });
    }
    setShowChangeCallLocationModal(false);
    setPendingCallLocationChange(null);
  }, [pendingCallLocationChange]);

  // ── Map Initialization ──────────────────────────────────────────

  useEffect(() => {
    // Create the CurrentMap singleton
    setupMapCore();

    // Initialize Google Maps and all overlays
    initAutocompleteTemp();

    if (callCardId) {
      // MapById: fetch call card, then init map centered on its location
      const mt = isCad ? ModuleType.cad : ModuleType.mdt;
      api.getCallCardById(callCardId, false, (result: any) => {
        const CC = new (window as any).CallCard(result, mt, false);
        // Closed CAD calls aren't rendered by the live marker pipeline, so we
        // need to drop a standalone pin to show the location.
        const needsStandaloneMarker = isCad && CC.ccStatus === CallCardStatus.CASE_CLOSED;
        // The call's stored coordinate is a permanent fact; never re-geocode it on render.
        initMap(CC.callLocFormattedAddress, false, true, needsStandaloneMarker,
          { lat: CC.latitude, lng: CC.longitude, companyId: CC.company,
            addressVerificationStatus: CC.addressVerificationStatus });
        setUpMapOverlay();
        resetMapRefresh();
        setMapScreenLoaded(true);
        setMapInitialized(true);
      });
    } else if (address) {
      // MapByAddress: the trusted-location lookup is session-scoped, so the session company.
      initMap(decodeURIComponent(address), false, false, true, { companyId: getCurrentCompanyId() });
      setUpMapOverlay();
      resetMapRefresh();
      setMapScreenLoaded(true);
      setMapInitialized(true);
    } else {
      // Default map: center on user location
      initMap(null, true, true);
      setUpMapOverlay();
      resetMapRefresh();
      setMapScreenLoaded(true);
      setMapInitialized(true);
    }

    // Cleanup is handled by useGoogleMap hook
  }, []); // Run once on mount

  // ── Expose MapController for legacy code ────────────────────────

  useEffect(() => {
    if (!CurrentMap) return;

    const controller: any = {};

    // State properties as getter/setter pairs backed by React state
    Object.defineProperty(controller, 'areMapOptionsShown', {
      get: () => areMapOptionsShown,
      set: (val: boolean) => setAreMapOptionsShown(val),
    });
    Object.defineProperty(controller, 'areMapOverlayOptionsShown', {
      get: () => areMapOverlayOptionsShown,
      set: (val: boolean) => setAreMapOverlayOptionsShown(val),
    });
    Object.defineProperty(controller, 'isEditingPolygonOverlay', {
      get: () => isEditingPolygonOverlay,
      set: (val: boolean) => setIsEditingPolygonOverlay(val),
    });
    Object.defineProperty(controller, 'isSelectedIsVisible', {
      get: () => isSelectedIsVisible,
      set: (val: boolean) => setIsSelectedIsVisible(val),
    });
    Object.defineProperty(controller, 'isSelectedPolygonPatrolZone', {
      get: () => isSelectedPolygonPatrolZone,
      set: (val: boolean) => setIsSelectedPolygonPatrolZone(val),
    });
    Object.defineProperty(controller, 'isSelectedPolygonEditable', {
      get: () => isSelectedPolygonEditable,
      set: (val: boolean) => setIsSelectedPolygonEditable(val),
    });
    Object.defineProperty(controller, 'isSelectedPolygonDraggable', {
      get: () => isSelectedPolygonDraggable,
      set: (val: boolean) => setIsSelectedPolygonDraggable(val),
    });
    Object.defineProperty(controller, 'selectedPolygonLabelText', {
      get: () => selectedPolygonLabelText,
      set: (val: string) => setSelectedPolygonLabelText(val),
    });
    Object.defineProperty(controller, 'selectedPolygonColor', {
      get: () => selectedPolygonColor,
      set: (val: string) => setSelectedPolygonColor(val),
    });
    Object.defineProperty(controller, 'isPolygonModified', {
      get: () => isPolygonModified,
      set: (val: boolean) => setIsPolygonModified(val),
    });
    Object.defineProperty(controller, 'addCustomLocationDialogLocation', {
      get: () => addCustomLocationDialogLocation,
      set: (val: string) => setAddCustomLocationDialogLocation(val),
    });
    Object.defineProperty(controller, 'addCustomLocationDialogCoordinates', {
      get: () => addCustomLocationDialogCoordinates,
      set: (val: string) => setAddCustomLocationDialogCoordinates(val),
    });
    Object.defineProperty(controller, 'showAddCustomLocationModal', {
      get: () => showAddCustomLocationModal,
      set: (val: boolean) => setShowAddCustomLocationModal(val),
    });
    Object.defineProperty(controller, 'showTrackMeButtons', {
      get: () => showTrackMeButtons,
      set: (val: boolean) => setShowTrackMeButtons(val),
    });
    Object.defineProperty(controller, 'isTrackingMe', {
      get: () => isTrackingMe,
      set: (val: boolean) => setIsTrackingMe(val),
    });
    Object.defineProperty(controller, 'showChangeCallLocationModal', {
      get: () => showChangeCallLocationModal,
      set: (val: boolean) => setShowChangeCallLocationModal(val),
    });
    Object.defineProperty(controller, 'pendingCallLocationChange', {
      get: () => pendingCallLocationChange,
      set: (val: any) => setPendingCallLocationChange(val),
    });
    Object.defineProperty(controller, 'showDeleteAreaModal', {
      get: () => showDeleteAreaModal,
      set: (val: boolean) => setShowDeleteAreaModal(val),
    });
    Object.defineProperty(controller, 'showColorPicker', {
      get: () => showColorPicker,
      set: (val: boolean) => setShowColorPicker(val),
    });
    Object.defineProperty(controller, 'showKmlModal', {
      get: () => showKmlModal,
      set: (val: boolean) => setShowKmlModal(val),
    });

    // Methods
    controller.openNav = openNav;
    controller.closeNav = closeNavAndOverlayOptions;
    controller.openOverlayOptions = openOverlayOptions;
    controller.addPolygonOverlay = addPolygonOverlay;
    controller.removePolygonOverlay = removePolygonOverlay;
    controller.savePolygons = savePolygons;
    controller.onFilterCheckboxChange = onFilterCheckboxChange;
    controller.onLayerSwitchChange = onLayerSwitchChange;
    controller.onTrackMeClick = onTrackMeClick;
    controller.onCenterMeClick = onCenterMeClick;
    controller.movePolygonOverlayToFront = movePolygonOverlayToFront;
    controller.movePolygonOverlayToBack = movePolygonOverlayToBack;
    controller.manageMapOverlayKml = () => setShowKmlModal(true);
    controller.closeAddCustomLocationModal = closeAddCustomLocationModal;
    controller.saveCustomLocationFromDialog = saveCustomLocationFromDialog;
    controller.closeColorPicker = closeColorPicker;
    controller.handleColorSelect = handleColorSelect;
    // focusCustomLocationInput: modal auto-focuses via rAF, so this is a no-op
    controller.focusCustomLocationInput = () => {};

    CurrentMap.mapController = controller;
  }); // no dependency array — re-runs every render to keep controller current

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div id="map-vue" className="map-page-container">
      {/* ── Side nav: Filters ── */}
      <div
        className="map-side-nav"
        style={{ width: areMapOptionsShown ? '250px' : '0' }}
      >
        <a className="closebtn" onClick={closeNavAndOverlayOptions}>&times;</a>
        <label className="btn MapFilter">
          <span className="iconOfficerMe"></span>You
        </label>
        <label className="btn MapFilter">
          <span className="iconOfficersAvailable"></span>
          <input id="mapFilterOfficersAvailable" type="checkbox" onChange={onFilterCheckboxChange} />
          Available {officerPluralLabel}
        </label>
        <label className="btn MapFilter">
          <span className="iconOfficersOnCall"></span>
          <input id="mapFilterOfficersOnCall" type="checkbox" onChange={onFilterCheckboxChange} />
          {' '}Assigned {officerPluralLabel}
        </label>
        <label className="btn MapFilter">
          <span className="iconCallCardsActive"></span>
          <input id="mapFilterCallCardsActive" type="checkbox" onChange={onFilterCheckboxChange} />
          {' '}Active Calls
        </label>
        <label className="btn MapFilter">
          <span className="iconCallCardsPending"></span>
          <input id="mapFilterCallCardsPending" type="checkbox" onChange={onFilterCheckboxChange} />
          {' '}Pending Calls
        </label>
        <label className="btn MapFilter">
          <span className="iconGuardTourStopCompleted"></span>
          <input id="mapFilterGuardToursComplete" type="checkbox" onChange={onFilterCheckboxChange} />
          {' '}Stops Completed
        </label>
        <label className="btn MapFilter">
          <span className="iconGuardTourStopClaimed"></span>
          <input id="mapFilterGuardToursClaimed" type="checkbox" onChange={onFilterCheckboxChange} />
          {' '}Stops In Progress
        </label>
        <label className="btn MapFilter">
          <span className="iconGuardTourStopUnclaimed"></span>
          <input id="mapFilterGuardToursUnClaimed" type="checkbox" onChange={onFilterCheckboxChange} />
          {' '}Stops Pending
        </label>
        <div className="traffic-section">
          <div className="traffic-settings-head"></div>
          <ul>
            <li className="btn MapFilter">
              <label className="switch">
                <input type="checkbox" id="bikeLayerSwitch" onChange={() => onLayerSwitchChange('bike')} />
                <span className="slider round"></span>
              </label>
              Bike Layer
            </li>
            <li className="btn MapFilter">
              <label className="switch">
                <input type="checkbox" id="transitLayerSwitch" onChange={() => onLayerSwitchChange('transit')} />
                <span className="slider round"></span>
              </label>
              Transit Layer
            </li>
            <li className="btn MapFilter">
              <label className="switch">
                <input type="checkbox" id="trafficLayerSwitch" onChange={() => onLayerSwitchChange('traffic')} />
                <span className="slider round"></span>
              </label>
              Traffic Layer
            </li>
          </ul>
        </div>
      </div>

      {/* ── Side nav: Overlay options ── */}
      <div
        className="map-side-nav"
        style={{ width: areMapOverlayOptionsShown ? '250px' : '0' }}
      >
        <a className="closebtn" onClick={closeNavAndOverlayOptions}>&times;</a>
        <div className="traffic-section">
          <div className="traffic-settings-head"></div>
          <ul>
            <li className="btn MapFilter">
              <button className="btn btn-success" onClick={addPolygonOverlay}>
                <i className="fa fa-plus-circle"></i> Add Area
              </button>
            </li>
            {isEditingPolygonOverlay && (
              <li className="btn MapFilter">
                <div className="overlay-label-div" style={{ marginBottom: 10 }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Label"
                    value={selectedPolygonLabelText}
                    onChange={(e) => setSelectedPolygonLabelText(e.target.value)}
                  />
                </div>
                {hasMapOverlayAreaVisibilityPermission && (
                  <div style={{ marginBottom: 7 }}>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={isSelectedIsVisible}
                        onChange={(e) => setIsSelectedIsVisible(e.target.checked)}
                      />
                      <span className="slider round"></span>
                    </label>
                    <div className="switch-label">Visible</div>
                  </div>
                )}
                <div style={{ marginBottom: 7 }}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isSelectedPolygonPatrolZone}
                      onChange={(e) => {
                        setIsSelectedPolygonPatrolZone(e.target.checked);
                        setIsPolygonModified(true);
                      }}
                    />
                    <span className="slider round"></span>
                  </label>
                  <div className="switch-label">
                    Patrol Zone{' '}
                    <Tooltip
                      content="When a Call is sent or updated, and its Call Location is in a Patrol Zone, the Patrol Zone's label will show under the Call Location."
                      position="top"
                      maxWidth="280px"
                    />
                  </div>
                </div>
                <div style={{ marginBottom: 7 }}>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isSelectedPolygonEditable}
                      onChange={(e) => setIsSelectedPolygonEditable(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <div className="switch-label">Change Shape</div>
                </div>
                <div>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isSelectedPolygonDraggable}
                      onChange={(e) => setIsSelectedPolygonDraggable(e.target.checked)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <div className="switch-label">Drag Shape</div>
                </div>
                <div className="overlay-label-div" style={{ marginBottom: 10 }}>
                  <button
                    type="button"
                    className="btn"
                    style={{ backgroundColor: selectedPolygonColor || undefined, width: '30px', height: '30px', border: '1px solid #ccc' }}
                    onClick={() => setShowColorPicker(true)}
                  ></button>
                  <div className="switch-label" style={{ marginLeft: 7 }}>Color</div>
                </div>
                <div>
                  <button className="btn btn-warning" onClick={movePolygonOverlayToFront}>
                    <i className="fa fa-caret-up"></i> Move to Front
                  </button>
                </div>
                <div>
                  <button className="btn btn-warning" onClick={movePolygonOverlayToBack}>
                    <i className="fa fa-caret-down"></i> Move to Back
                  </button>
                </div>
                <div>
                  <button className="btn btn-danger" onClick={removePolygonOverlay}>
                    <i className="fa fa-trash"></i> Delete Area
                  </button>
                </div>
              </li>
            )}
            <li className="btn MapFilter">
              <button className="btn btn-success" disabled={!isPolygonModified} onClick={savePolygons}>
                <i className="fa fa-cloud-upload"></i> Save Areas
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* ── Main map area ── */}
      <div style={{ marginLeft: (areMapOptionsShown || areMapOverlayOptionsShown) ? '250px' : '0' }}>
        <div id="map" onContextMenu={(e) => e.preventDefault()}></div>
        <div className="navbar navbar-nav navbar-fixed-bottom" id="myNavbar">
          <input id="pac-input" className="form-control" type="text" placeholder="Search Address" />
          <span className="btn btn-warning" onClick={openNav}>Filter</span>
          {showOverlayButton && (
            <span className="btn btn-warning" onClick={openOverlayOptions}>Overlay</span>
          )}
          {showOverlayButton && (
            <span className="btn btn-warning" onClick={() => setShowKmlModal(true)}>KML/KMZ</span>
          )}
          {showTrackMeButtons && (
            <button
              className={`btn ${isTrackingMe ? 'btn-success' : 'btn-danger'}`}
              id="btnTrackMe"
              onClick={onTrackMeClick}
            >
              {isTrackingMe ? 'Tracking' : 'Track Me'}
            </button>
          )}
          {showTrackMeButtons && (
            <button className="btn btn-default" id="btnCurrentLoc" onClick={onCenterMeClick}>
              Center Me
            </button>
          )}
        </div>
      </div>

      {/* ── Modals ── */}
      <ConfirmModal
        open={showAddCustomLocationModal}
        title="Add Custom Location"
        confirmText="Save"
        cancelText="Cancel"
        confirmClass="primary"
        confirmDisabled={isSavingCustomLocation}
        busy={isSavingCustomLocation}
        theme={theme}
        onConfirm={saveCustomLocationFromDialog}
        onCancel={closeAddCustomLocationModal}
      >
        <ul className="fieldlist">
          <li>
            <span className="input-group">Location: </span>
            <input
              type="text"
              className="form-control"
              value={addCustomLocationDialogLocation || ''}
              onChange={(e) => setAddCustomLocationDialogLocation(e.target.value)}
            />
          </li>
          <li>
            <span className="input-group">Coordinates: </span>
            <input
              type="text"
              className="form-control"
              value={addCustomLocationDialogCoordinates || ''}
              disabled
            />
          </li>
        </ul>
      </ConfirmModal>
      <ConfirmModal
        open={showDeleteAreaModal}
        title="Remove Area?"
        message="Are you sure you want to remove this area?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        theme={theme}
        onConfirm={confirmDeleteArea}
        onCancel={() => setShowDeleteAreaModal(false)}
      />
      <ConfirmModal
        open={showChangeCallLocationModal}
        title="Change Call Location?"
        message={changeCallLocationMessage}
        confirmText="Yes"
        cancelText="No"
        confirmClass="primary"
        theme={theme}
        onConfirm={confirmChangeCallLocation}
        onCancel={cancelChangeCallLocation}
      />
      <ColorPicker
        open={showColorPicker}
        currentColor={selectedPolygonColor || undefined}
        customTitle="Area Color"
        showRemove={false}
        onSelect={handleColorSelect}
        onClose={closeColorPicker}
      />
      <PageModal
        open={showKmlModal}
        title="Manage KML"
        modalId="mapManageKml"
        onClose={() => setShowKmlModal(false)}
      >
        {showKmlModal && <MapOverlayKmlScreen />}
      </PageModal>
    </div>
  );
}

// ── Module-level State ───────────────────────────────────────────────

let mapScreenContainer: HTMLElement | null = null;

// ── Bridge Functions (for legacy callers outside the React router) ───

function initMapScreenReact(showTrackMeButtons?: boolean): void {
  // If MapScreen is already rendered by the React router, skip mounting
  // a second instance. The router-rendered component handles everything.
  if (document.getElementById('map-vue')) return;

  if (mapScreenContainer) {
    destroyReactComponent(mapScreenContainer);
    mapScreenContainer = null;
  }

  const mapVue = document.getElementById('map-vue');
  if (!mapVue) return;

  let reactRoot = document.getElementById('map-react-root');
  if (!reactRoot) {
    reactRoot = document.createElement('div');
    reactRoot.id = 'map-react-root';
    mapVue.appendChild(reactRoot);
  }

  mapScreenContainer = reactRoot;

  renderReactComponent(
    MapScreen,
    { showTrackMeButtons: showTrackMeButtons !== undefined ? showTrackMeButtons : true },
    mapScreenContainer
  );
}

function destroyMapScreen(): void {
  if (mapScreenContainer) {
    destroyReactComponent(mapScreenContainer);
    mapScreenContainer = null;
  }
}

// ── Window Registration ─────────────────────────────────────────────

(window as any).MapScreenReact = MapScreen;
(window as any).initMapScreenReact = initMapScreenReact;
(window as any).destroyMapScreen = destroyMapScreen;

// ── Exports ─────────────────────────────────────────────────────────

export { initMapScreenReact, destroyMapScreen };
